// RecentCelebrations — public homepage widget displaying the latest
// milestone celebrations across the platform.
//
// Why it's here: viewers who arrive from a `via=@GlobalEyesDOTtv`
// tweet land on the home page and see a *stream of milestone moments*
// happening right now. Pure social proof — the platform feels alive,
// other producers are crushing it, and the new arrival is more likely
// to convert (or at least stay long enough to discover a video).
//
// Self-hides when the API returns zero items so an empty stream
// never embarrasses us during the cold-start phase.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PartyPopper, MapPin } from "lucide-react";
import api from "../../lib/apiClient";

const THRESHOLD_LABEL = {
  10: "10 loops",
  100: "100 loops",
  1000: "1K loops",
  10000: "10K loops",
};

const THRESHOLD_PREFIX = (n) => {
  if (n >= 10000) return "🚀";
  if (n >= 1000) return "🎉";
  if (n >= 100) return "✨";
  return "🌱";
};

// Compact relative timestamp — "just now", "2m", "6h", "3d".
const relTime = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "";
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
};

const RecentCelebrations = () => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/x/celebrations/recent?limit=8")
      .then((r) => {
        if (!alive) return;
        setItems(r.data?.items || []);
      })
      .catch(() => { /* tolerate — empty state hides the widget */ })
      .finally(() => {
        if (alive) setLoaded(true);
      });
    return () => { alive = false; };
  }, []);

  if (!loaded || items.length === 0) return null;

  return (
    <section
      data-testid="recent-celebrations"
      className="mx-auto max-w-4xl mt-10 mb-6 px-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <PartyPopper className="h-4 w-4 text-amber-300" />
        <h2 className="text-white font-semibold text-base sm:text-lg">
          Recent celebrations
        </h2>
        <span className="text-gray-500 text-xs">· producers crushing milestones</span>
      </div>
      <div className="space-y-2">
        {items.map((c, i) => {
          const place = [c.city, c.country].filter(Boolean).join(", ");
          return (
            <Link
              key={`${c.video_id}-${c.threshold}-${i}`}
              to={`/video/${c.video_id}`}
              data-testid={`celebration-row-${i}`}
              className="flex items-center gap-3 rounded-lg border border-gray-800/70 bg-gray-900/60 hover:bg-gray-900 hover:border-amber-500/40 transition-colors px-3 py-2"
            >
              <span className="text-2xl shrink-0" aria-hidden="true">
                {THRESHOLD_PREFIX(c.threshold || 0)}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm leading-tight">
                  <span className="font-semibold text-amber-300">
                    @{c.username}
                  </span>
                  <span className="text-gray-400"> just hit </span>
                  <span className="font-semibold text-white">
                    {THRESHOLD_LABEL[c.threshold] || `${c.threshold} loops`}
                  </span>
                  <span className="text-gray-400"> on </span>
                  <span className="text-white truncate">
                    "{c.title || "their video"}"
                  </span>
                </p>
                <p className="text-gray-500 text-[11px] mt-0.5 flex items-center gap-1">
                  {place && (
                    <>
                      <MapPin className="h-3 w-3" />
                      <span>{place}</span>
                      <span>·</span>
                    </>
                  )}
                  <span>{relTime(c.celebrated_at)} ago</span>
                </p>
              </div>
              <span className="text-emerald-400 text-xs whitespace-nowrap shrink-0 group-hover:text-emerald-300">
                Watch →
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default RecentCelebrations;
